import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import "../Revenue/revenue.css";
import "../Withdrawals/withdrawals.css";
import { resolveImg } from "../../Api/Api";
import { errorMessage } from "../Revenue/revenueApi";
import { Avatar, Loading, Pager } from "../Revenue/parts";
import { ConfirmModal, fmtTime, num, useDebounced, useNotice } from "../Withdrawals/payoutParts";
import { KycBadge } from "../Withdrawals/Withdrawals";
import { KYC_ACTIONS, KYC_TABS, listKycUsers, setKycStatus } from "./kycApi";

const LIMIT = 30;
const TAB_KEYS = KYC_TABS.map((t) => t.key);

const CONFIRM_TEXT = {
  allowed: "The user can request diamond withdrawals from now on. Allow only after checking the national ID.",
  not_allowed: "New withdrawal requests are refused for this user until you allow them. Requests already in the queue stay as they are.",
  review: "Moves the user back to the review list. New withdrawal requests are refused while in review.",
};

/// KYC review for withdrawals: users who uploaded their national ID wait in
/// "review"; allow or refuse them, or move them back to review.
export default function Kyc() {
  const [params, setParams] = useSearchParams();
  const [status, setStatus] = useState(TAB_KEYS.includes(params.get("status")) ? params.get("status") : "review");
  const [q, setQ] = useState("");
  const [rows, setRows] = useState(null);
  const [meta, setMeta] = useState(null);
  const [counts, setCounts] = useState({});
  const [error, setError] = useState("");
  const [notice, setNotice] = useNotice();
  const [confirm, setConfirm] = useState(null); // {user, to}
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState("");
  const seq = useRef(0);
  const term = useDebounced(q.trim(), 400);
  const filterKey = `${status}|${term}`;
  const [pager, setPager] = useState({ key: filterKey, page: 1 });
  const page = pager.key === filterKey ? pager.page : 1;

  const load = useCallback(async () => {
    const my = ++seq.current;
    setError("");
    try {
      const res = await listKycUsers({ status, name: term, page, limit: LIMIT });
      if (my !== seq.current) return;
      setRows(res?.data || []);
      setMeta(res?.meta || null);
    } catch (e) {
      if (my !== seq.current) return;
      setRows([]);
      setMeta(null);
      setError(errorMessage(e, "Could not load the users"));
    }
  }, [status, term, page]);

  /// Totals per status (no name filter) — one small request each.
  const loadCounts = useCallback(async () => {
    const out = {};
    await Promise.all(
      TAB_KEYS.map(async (k) => {
        try {
          const res = await listKycUsers({ status: k, page: 1, limit: 1 });
          out[k] = Number(res?.meta?.total) || 0;
        } catch (e) {
          out[k] = null;
        }
      })
    );
    setCounts(out);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    loadCounts();
  }, [loadCounts]);

  useEffect(() => {
    setParams(status === "review" ? {} : { status }, { replace: true });
  }, [status, setParams]);

  const apply = async () => {
    if (!confirm) return;
    setBusy(true);
    setActionError("");
    try {
      await setKycStatus(confirm.user.id, confirm.to);
      const label = KYC_ACTIONS.find((a) => a.key === confirm.to)?.label || confirm.to;
      setNotice(`@${confirm.user.name || confirm.user.id}: ${label === "Allow" ? "allowed" : label.toLowerCase()}.`);
      setRows((list) => (list || []).filter((r) => r.id !== confirm.user.id));
      setConfirm(null);
      load();
      loadCounts();
    } catch (e) {
      setActionError(errorMessage(e, "Could not change the KYC status"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rv-wrap wd-page">
      <div className="d-flex flex-wrap align-items-end justify-content-between gap-3 mb-4">
        <div>
          <h2 className="rv-title mb-1">KYC review</h2>
          <p className="rv-sub">National ID checks for diamond withdrawals.</p>
        </div>
        <button
          type="button"
          className="rv-btn ghost"
          onClick={() => {
            load();
            loadCounts();
          }}
        >
          Refresh
        </button>
      </div>

      <div className="wd-banner mb-3">
        Withdrawals require the KYC status <b>Allowed</b>: the server refuses a new withdrawal request from a user in
        review or not allowed. A user who uploads the national ID (front + back) in the app lands in{" "}
        <b>Waiting for review</b>. Changing the status here does not touch requests already in the{" "}
        <Link to="/dashboard/withdrawals">withdrawal queue</Link>.
      </div>

      {notice && <div className="rv-success mb-3">{notice}</div>}
      {error && <div className="rv-error mb-3">{error}</div>}

      <div className="rv-panel">
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
          <div className="rv-tabs">
            {KYC_TABS.map((t) => (
              <button
                key={t.key}
                type="button"
                className={`rv-tab ${status === t.key ? "active" : ""}`}
                onClick={() => setStatus(t.key)}
              >
                {t.label}{" "}
                <span className="wd-count">{counts[t.key] === null || counts[t.key] === undefined ? "…" : num(counts[t.key], 0)}</span>
              </button>
            ))}
          </div>
          <input className="rv-input wd-search" placeholder="Search by name, nickname or #id" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>

        {rows === null ? (
          <Loading />
        ) : !rows.length ? (
          <div className="rv-empty">{term ? "No user matches." : status === "review" ? "Nobody is waiting for review." : "No users here."}</div>
        ) : (
          <div className="rv-table-wrap">
            <table className="rv-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>National ID</th>
                  <th className="rv-num">Followers</th>
                  <th>Joined</th>
                  <th>Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((u) => (
                  <tr key={u.id}>
                    <td>
                      <Link className="rv-user text-white text-decoration-none" to={`/dashboard/user-management/${u.id}`}>
                        <Avatar img={u.img} name={u.name} />
                        <div style={{ minWidth: 0 }}>
                          <div className="rv-user-name">{u.name ? `@${u.name}` : `User #${u.id}`}</div>
                          <div className="rv-user-sub">
                            #{u.id}
                            {u.nick_name ? ` · ${u.nick_name}` : ""}
                          </div>
                        </div>
                      </Link>
                    </td>
                    <td>
                      <IdImages user={u} />
                    </td>
                    <td className="rv-num">{num(u.followers, 0)}</td>
                    <td>{fmtTime(u.created_at)}</td>
                    <td>
                      <KycBadge status={u.isCanExchange || status} />
                    </td>
                    <td className="wd-actions-cell text-end">
                      {KYC_ACTIONS.filter((a) => a.key !== (u.isCanExchange || status)).map((a) => (
                        <button
                          key={a.key}
                          type="button"
                          className={`rv-btn rv-btn-sm ${a.tone}`}
                          onClick={() => {
                            setActionError("");
                            setConfirm({ user: u, to: a.key });
                          }}
                        >
                          {a.label}
                        </button>
                      ))}
                      <Link className="rv-btn ghost rv-btn-sm ms-2" to={`/dashboard/withdrawals?status=all&q=${u.id}`}>
                        Withdrawals
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <Pager meta={meta} onPage={(p) => setPager({ key: filterKey, page: p })} />
      </div>

      {confirm && (
        <ConfirmModal
          title={`${KYC_ACTIONS.find((a) => a.key === confirm.to)?.label} — @${confirm.user.name || confirm.user.id}`}
          confirmText={KYC_ACTIONS.find((a) => a.key === confirm.to)?.label}
          tone={confirm.to === "allowed" ? "success" : confirm.to === "not_allowed" ? "danger" : ""}
          busy={busy}
          error={actionError}
          onClose={() => setConfirm(null)}
          onConfirm={apply}
        >
          <p className="rv-sub">{CONFIRM_TEXT[confirm.to]}</p>
        </ConfirmModal>
      )}
    </div>
  );
}

/// Front / back of the national ID: `kyc_front_url` / `kyc_back_url` from
/// Admin-users (signed links valid 15 min — Refresh the page for new ones).
function IdImages({ user }) {
  const sent = "kyc_front_url" in user || "kyc_back_url" in user;
  const imgs = [
    ["Front", user.kyc_front_url || user.front_card_image],
    ["Back", user.kyc_back_url || user.back_card_image],
  ].filter(([, v]) => v);
  if (!imgs.length) {
    return sent ? (
      <span className="text-grey">Not uploaded</span>
    ) : (
      <span className="text-grey" title="This backend does not return the ID images yet">Not sent by the API</span>
    );
  }
  return (
    <div className="wd-id-imgs">
      {imgs.map(([label, src]) => (
        <a key={label} href={resolveImg(src)} target="_blank" rel="noopener noreferrer" title={`${label} of the ID`}>
          <img src={resolveImg(src)} alt={`${label} of the ID`} />
        </a>
      ))}
    </div>
  );
}
